import { NYDtFormat, StatementPeriodOption } from '@/components/modal/StatementModal';
import { portalAPI } from '@/helpers/environmentHelper';
import { logToServer } from '@/services/LogService';
import { useAppDispatch } from '@/state/hooks';
import { useLocale } from '@react-aria/i18n';
import axios from 'axios';
import { differenceInDays, format, isBefore, subDays } from 'date-fns';
import { Dispatch, SetStateAction, useCallback, useMemo } from 'react';
import { getDateFormat } from '../format';
import { useSso } from '../providers/SSOProvider';
import { use401Error } from './usePortalAuthentication';
import useToast from './useToast';

export interface UseStatementFilterProps {
    isLoading: boolean;
    setIsLoading: Dispatch<SetStateAction<boolean>>;
    setXlsContent: Dispatch<SetStateAction<Uint8Array | null>>;
    setPdfContent: Dispatch<SetStateAction<Uint8Array | null>>;
    setStatementDetails: Dispatch<SetStateAction<any>>;
    selectedPeriod: StatementPeriodOption;
    startDate: Date;
    endDate: Date;
}

export interface UseStatementFilterReturn {
    isLoadingLastEOD: boolean;
    disabledDateInputs: { startDate: boolean; endDate: boolean };
    defaultDatePickerProps: { locale: string; minTime: Date; maxDate: Date; maxTime: Date };
    lastCompletedTradeDate: Date;
    generateStatement: (fileType: 'pdf' | 'xls') => Promise<void>;
}

export const useStatementFilter = (props: UseStatementFilterProps): UseStatementFilterReturn => {
    const { selectedPeriod, startDate, endDate, setIsLoading, setXlsContent, setPdfContent, setStatementDetails } =
        props;
    const dispatch = useAppDispatch();

    const { locale } = useLocale();
    const { jwt, lastEODApi, foundEntity } = useSso();
    const { on401Error } = use401Error();

    const { data: lastEodData, isLoading: isLoadingLastEOD } = lastEODApi;

    const [toastError, toastSuccess] = useToast();
    const dateFormatPattern = getDateFormat(locale);

    const lastTradeDate = useMemo(() => {
        const now = new Date();
        const newYorkNow = new Date(NYDtFormat.format(now));
        const newYork5pm = new Date(newYorkNow).setHours(17, 0, 0, 0);

        if (isBefore(newYorkNow, newYork5pm)) return subDays(newYorkNow, 1);
        else return now;
    }, []);

    const disabledDateInputs = useMemo(() => {
        if (selectedPeriod.value === 'custom') return { startDate: false, endDate: false };
        else if (selectedPeriod.value === 'daily') return { startDate: false, endDate: true };
        else return { startDate: true, endDate: true };
    }, [selectedPeriod]);

    const { timeZone } = Intl.DateTimeFormat().resolvedOptions();

    const lastCompletedTradeDate = useMemo(() => {
        return new Date(lastEodData || lastTradeDate);
    }, [lastEodData, lastTradeDate, timeZone]);

    const defaultDatePickerProps = useMemo(
        () => ({
            locale: locale.includes('en') ? 'en' : locale,
            minTime: lastCompletedTradeDate,
            maxDate: lastCompletedTradeDate,
            maxTime: lastCompletedTradeDate,
            dateFormat: dateFormatPattern,
            disabledKeyboardNavigation: true
        }),
        [lastCompletedTradeDate, locale]
    );

    const handleApiError = useCallback(
        (error) => {
            toastError({ title: error.name, body: error.message });
            setIsLoading(false);
        },
        [setIsLoading]
    );

    const generateStatement = useCallback(
        async (fileType: 'pdf' | 'xls') => {
            setIsLoading(true);
            setXlsContent(null);
            setPdfContent(null);
            try {
                if (foundEntity) {
                    if (isBefore(endDate, startDate))
                        handleApiError({
                            title: 'Invalid Request',
                            message: 'Statement end date cannot be the past of start date.'
                        });
                    else if (differenceInDays(endDate, startDate) > 30)
                        handleApiError({
                            title: 'Invalid Request',
                            message: 'Statement period cannot be more than 31 days.'
                        });
                    else {
                        const generateRequest: any = await axios.post(
                            portalAPI('/api/statement/generate'),
                            {
                                type: 'client',
                                entityId: foundEntity.entityId,
                                from: format(startDate, 'yyyy-MM-dd'),
                                to: format(endDate, 'yyyy-MM-dd'),
                                fileType
                            },
                            { headers: { Authorization: `Bearer ${jwt}` } }
                        );
                        setStatementDetails({
                            id: generateRequest?.data?.id,
                            state: generateRequest?.data?.state,
                            fileType,
                            startDate,
                            endDate,
                            entityId: foundEntity.entityId
                        });
                    }
                }
            } catch (error: any) {
                on401Error(error, () => {
                    logToServer(
                        'error',
                        `Unauthenticated access on /api/statement/generate. JWT may be invalid or expired.`
                    );
                });
                handleApiError({
                    title: error.name || 'Unknown Error',
                    body: 'Statement could not be generated for undetermined reasons. Please reach out to support.'
                });
                setIsLoading(false);
            }
        },
        [
            startDate,
            endDate,
            jwt,
            foundEntity,
            dispatch,
            handleApiError,
            setIsLoading,
            setStatementDetails,
            setXlsContent
        ]
    );

    return {
        isLoadingLastEOD,
        disabledDateInputs,
        defaultDatePickerProps,
        lastCompletedTradeDate,
        generateStatement
    };
};
